import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { track, trackFirstTime } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddRoomModal = ({ isOpen, onClose, onSuccess, propertyId, token }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: propertyId,
    room_number: '',
    floor: '',
    room_type: 'Single',
    total_beds: 1,
    rent_per_bed: '',
    deposit_amount: '',
    amenities: []
  });

  const roomTypes = ['Single', 'Double', 'Triple', 'Dormitory'];
  const amenitiesList = ['AC', 'WiFi', 'Attached Bathroom', 'TV', 'Fridge', 'Balcony'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/properties/${propertyId}/rooms`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.ROOM, { action: 'created', property_id: propertyId });
      track('room_created', { property_id: propertyId });
      trackFirstTime('first_room_added', { property_id: propertyId });

      toast({
        title: "Room Added!",
        description: "New room has been added successfully"
      });

      onSuccess();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add room",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function resetForm() {
    setFormData({
      property_id: propertyId,
      room_number: '',
      floor: '',
      room_type: 'Single',
      total_beds: 1,
      rent_per_bed: '',
      amenities: []
    });
  }

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add New Room</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Room Number *</Label>
              <Input
                value={formData.room_number}
                onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                placeholder="101, 201A, etc."
                required
              />
            </div>

            <div>
              <Label>Floor *</Label>
              <Input
                value={formData.floor}
                onChange={(e) => setFormData({...formData, floor: e.target.value})}
                placeholder="Ground, 1st, 2nd, etc."
                required
              />
            </div>

            <div>
              <Label>Room Type *</Label>
              <select
                value={formData.room_type}
                onChange={(e) => setFormData({...formData, room_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {roomTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Total Beds *</Label>
              <Input
                type="number"
                value={formData.total_beds}
                onChange={(e) => setFormData({...formData, total_beds: parseInt(e.target.value)})}
                min="1"
                max="10"
                required
              />
            </div>

            <div className="col-span-2">
              <Label>Amenities</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {amenitiesList.map(amenity => (
                  <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">{amenity}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Adding...' : 'Add Room'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRoomModal;
